import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal, Typography, Space, Empty, Spin, cn, Tag } from '../ui';
import { ExternalLink, Route as RouteIcon, Sparkles, Camera, ChevronLeft } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap, useMapEvents } from 'react-leaflet';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../lib/mapTiles';
import L from 'leaflet';
import { request } from '../api/client';
import { formatIst, formatIstDate, formatIstTime, toIstDateString } from '../lib/time';
import { PALETTE } from '../lib/palette';

delete (L.Icon.Default.prototype as any)._getIconUrl;
const dot = (color: string) => L.divIcon({
  className: '',
  html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`,
  iconSize: [14, 14], iconAnchor: [7, 7],
});
// A numbered stop marker — the stop's position in the day's sequence, so a marker on the map lines up
// with the same-numbered row in the timeline (1 = first action of the day … through check-out).
// `active` enlarges it + adds a colour-matched halo so the selected stop pops when picked from either side.
const numberedIcon = (color: string, n: number, active = false) => {
  const sz = active ? 30 : 23;
  const halo = active ? `0 0 0 5px ${color}59,0 2px 7px rgba(0,0,0,.55)` : '0 1px 4px rgba(0,0,0,.5)';
  return L.divIcon({
    className: '',
    html: `<div style="width:${sz}px;height:${sz}px;border-radius:50%;background:${color};border:${active ? 3 : 2}px solid #fff;box-shadow:${halo};display:flex;align-items:center;justify-content:center;color:#fff;font-size:${active ? 14 : 12}px;font-weight:800;line-height:1">${n}</div>`,
    iconSize: [sz, sz], iconAnchor: [sz / 2, sz / 2],
  });
};
// A small arrowhead rotated to the direction of travel, so the trail reads as "which way they went".
const arrowIcon = (deg: number) => L.divIcon({
  className: '',
  html: `<div style="transform:rotate(${deg}deg);width:16px;height:16px;display:flex;align-items:center;justify-content:center">
    <svg width="13" height="13" viewBox="0 0 24 24" fill={PALETTE.sky[500]} style="filter:drop-shadow(0 1px 1px rgba(0,0,0,.4))"><path d="M12 2 19 21 12 16 5 21Z"/></svg></div>`,
  iconSize: [16, 16], iconAnchor: [8, 8],
});
// Initial bearing (deg, clockwise from north) from a→b.
const bearingDeg = (a: [number, number], b: [number, number]) => {
  const rad = (d: number) => (d * Math.PI) / 180, deg = (r: number) => (r * 180) / Math.PI;
  const y = Math.sin(rad(b[1] - a[1])) * Math.cos(rad(b[0]));
  const x = Math.cos(rad(a[0])) * Math.sin(rad(b[0])) - Math.sin(rad(a[0])) * Math.cos(rad(b[0])) * Math.cos(rad(b[1] - a[1]));
  return (deg(Math.atan2(y, x)) + 360) % 360;
};

const fmtMin = (m: number) => (m < 60 ? `${Math.round(m)}m` : `${Math.floor(m / 60)}h ${Math.round(m) % 60}m`);
// ONE source of truth for stop colors — used by BOTH the timeline dots and the map markers so they
// match exactly (check-in green, visit blue, unplanned amber, check-out red).
const STOP_HEX: Record<string, string> = {
  CHECK_IN: '#10B981', VISIT: '#0EA5E9', UNPLANNED: '#F59E0B', CHECK_OUT: '#EF4444',
};
const LAST_SEEN_HEX = '#DB2777'; // distinct pink/magenta — the rep's latest known position ("last seen")
// "Last seen" beacon: a hollow pink ring larger than the stop markers, with a soft glow, so the latest
// position reads at a glance even when it overlays a numbered stop (the number shows through the middle).
const lastSeenIcon = L.divIcon({
  className: '',
  html: `<div style="width:38px;height:38px;border-radius:50%;border:3px solid ${LAST_SEEN_HEX};background:${LAST_SEEN_HEX}1f;box-shadow:0 0 0 4px ${LAST_SEEN_HEX}26,0 1px 4px rgba(0,0,0,.35);pointer-events:none"></div>`,
  iconSize: [38, 38], iconAnchor: [19, 19],
});
// Visit dots split by party type so retailers vs farmers are distinguishable (blue vs violet).
const VISIT_HEX: Record<string, string> = { RETAILER: '#0EA5E9', FARMER: '#8B5CF6' };
const stopColor = (s: any): string => (s.kind === 'VISIT' ? (VISIT_HEX[s.visitType] ?? STOP_HEX.VISIT) : (STOP_HEX[s.kind] ?? LAST_SEEN_HEX));
/** A day with several shifts has several punches. Only the first punch-in is its Start, and a punch-out is
 *  its End only when it is the last one AND no shift started after it (a shift still running has no End). */
const isDayStart = (stops: any[], i: number) => stops.findIndex((x) => x?.kind === 'CHECK_IN') === i;
const isDayEnd = (stops: any[], i: number) => {
  const kinds = stops.map((x) => x?.kind);
  return kinds.lastIndexOf('CHECK_OUT') === i && i > kinds.lastIndexOf('CHECK_IN');
};
function StatChip({ value, label, tone }: { value: ReactNode; label: string; tone?: 'amber' }) {
  return (
    <span className={cn('inline-flex items-baseline gap-1 rounded-md px-2 py-1', tone === 'amber' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-surface-2 text-content')}>
      <b className="tabular-nums">{value}</b><span className="text-2xs text-muted">{label}</span>
    </span>
  );
}

// Renders the day's numbered stop markers, FANNING OUT any that share the same coordinate (e.g. the
// last visit and the check-out, or two visits to the same shop) by a constant pixel offset so each stays
// visible + clickable at any zoom. Offsets are recomputed on zoom (pixel→latlng conversion needs the map).
function NumberedStops({ stops, activeStop, onPick, register }: {
  stops: any[]; activeStop: number | null; onPick: (i: number) => void; register: (i: number, m: any) => void;
}) {
  const map = useMap();
  const [, bump] = useState(0);
  useMapEvents({ zoomend: () => bump((n) => n + 1) }); // re-fan after a zoom (pixel offsets shift in latlng)

  // Group stop indices by rounded coordinate (~1 m) so co-located stops can be spread apart.
  const groups = new Map<string, number[]>();
  stops.forEach((s, i) => {
    if (s?.lat == null || s?.lng == null) return;
    const key = `${(+s.lat).toFixed(5)},${(+s.lng).toFixed(5)}`;
    (groups.get(key) ?? (groups.set(key, []), groups.get(key)!)).push(i);
  });

  return (
    <>
      {stops.map((s, i) => {
        if (s?.lat == null || s?.lng == null) return null;
        const key = `${(+s.lat).toFixed(5)},${(+s.lng).toFixed(5)}`;
        const grp = groups.get(key)!;
        let pos: [number, number] = [+s.lat, +s.lng];
        if (grp.length > 1) {
          // Fan the group's members evenly around the true point at a fixed pixel radius.
          const k = grp.indexOf(i);
          const R = 15 + (grp.length > 4 ? 5 : 0);
          const ang = (2 * Math.PI / grp.length) * k - Math.PI / 2;
          const base = map.latLngToLayerPoint([+s.lat, +s.lng]);
          const ll = map.layerPointToLatLng(L.point(base.x + R * Math.cos(ang), base.y + R * Math.sin(ang)));
          pos = [ll.lat, ll.lng];
        }
        return (
          <Marker
            key={`ns${i}`}
            position={pos}
            icon={numberedIcon(stopColor(s), i + 1, i === activeStop)}
            zIndexOffset={i === activeStop ? 1000 : 0}
            ref={(m: any) => { if (m) register(i, m); }}
            eventHandlers={{ click: () => onPick(i) }}
          >
            <Popup>
              <b>{i + 1}. {s.label}</b><br />
              {/* A day with several shifts has several punches: only its first punch-in is the Start and
                  only its last punch-out the End. */}
              {s.kind === 'CHECK_IN' ? (stops.findIndex((x) => x?.kind === 'CHECK_IN') === i ? 'Check-in · Start' : 'Check-in')
                : s.kind === 'CHECK_OUT' ? (isDayEnd(stops, i) ? 'Check-out · End' : 'Check-out')
                : s.kind === 'UNPLANNED' ? 'Unplanned stop · not a visit'
                : (s.visitType === 'RETAILER' ? 'Retailer' : s.visitType === 'FARMER' ? 'Farmer' : 'Visit')}
              {' · '}{formatIst(s.at)}
            </Popup>
          </Marker>
        );
      })}
    </>
  );
}

// Pans to + opens the popup of the currently-selected stop (picked from the timeline or another marker),
// so selecting stop 3 anywhere brings marker 3 to the centre and pops its label.
function FocusStop({ activeStop, getMarker }: { activeStop: number | null; getMarker: (i: number) => any }) {
  const map = useMap();
  useEffect(() => {
    if (activeStop == null) return;
    const m = getMarker(activeStop);
    if (!m) return;
    map.flyTo(m.getLatLng(), Math.max(map.getZoom(), 12), { duration: 0.6 });
    m.openPopup();
  }, [activeStop]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// Fits the map to all points + fixes sizing once the modal has opened.
function MapReady({ pts }: { pts: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => {
      map.invalidateSize();
      if (pts.length === 1) map.setView(pts[0], 15);
      else if (pts.length > 1) map.fitBounds(pts as any, { padding: [30, 30], maxZoom: 16 });
    }, 250);
    return () => clearTimeout(t);
  }, [map, pts]);
  return null;
}

/**
 * A staff member's field MOVEMENT over a period: GPS-trace polyline (with direction arrows) + visit,
 * attendance-punch and last-seen markers, plus a distance/stops/active-window summary. Reused by the
 * Attendance log (single day per row) and anywhere else that needs a route trail. `from === to` renders
 * the single date. Reads `GET /performance/sales/:spId/route` (gated DASH.SALES).
 */
export function RouteMapModal({ spId, name, from, to, onClose }: { spId: string; name: string; from: string; to: string; onClose: () => void }) {
  // This modal ALWAYS opens on a single day — the same thing the Attendance route button shows:
  // day summary, numbered stops, timeline. A caller may hand in a whole period (Sales Performance
  // "Track" passes the selected range); we then land on the most recent day that has activity and
  // offer the other days in a picker.
  //
  // The timeline stays strictly per-day. It is check-in → visits → check-out with per-leg travel vs
  // expected, so stitching several days together would invent a leg that is really the rep sleeping
  // at home. The whole-period trail is still available behind "Full range".
  const [activeDate, setActiveDate] = useState<string | null>(null);
  const [showFullRange, setShowFullRange] = useState(false);
  const isRange = from !== to;

  // The whole-period payload: drives the day list, and the trail when "Full range" is chosen.
  const { data: rangeData } = useQuery({
    queryKey: ['perf-route', spId, from, to],
    queryFn: () => request<any>({ url: `/performance/sales/${spId}/route`, params: { from, to } }),
    enabled: isRange,
  });
  // Days that actually have something to show — no point offering an empty date. Newest first.
  const dayOptions: string[] = useMemo(() => {
    if (!isRange || !rangeData) return [];
    const days = new Set<string>();
    for (const src of [rangeData.visits ?? [], rangeData.attendance ?? [], rangeData.trace ?? []]) {
      for (const x of src) if (x?.at) days.add(toIstDateString(new Date(String(x.at).replace(' ', 'T'))));
    }
    return [...days].sort((a, b) => (a < b ? 1 : -1));
  }, [rangeData, isRange]);

  // The date the modal is showing. An explicit pick wins; otherwise the newest day with activity.
  const defaultDay = isRange && !showFullRange && dayOptions.length > 0 ? dayOptions[0] : null;
  const timelineDate: string | null = activeDate ?? defaultDay ?? (isRange ? null : from);
  const isDay = timelineDate != null;

  // Map data follows the timeline: showing one day's stops over another day's trail would be a lie.
  const effFrom = timelineDate ?? from;
  const effTo = timelineDate ?? to;
  const { data, isLoading } = useQuery({
    queryKey: ['perf-route', spId, effFrom, effTo],
    queryFn: () => request<any>({ url: `/performance/sales/${spId}/route`, params: { from: effFrom, to: effTo } }),
  });

  // The analytics timeline + AI narrative for that date.
  const { data: day } = useQuery({
    queryKey: ['perf-day-summary', spId, timelineDate],
    queryFn: () => request<any>({ url: `/performance/sales/${spId}/day-summary`, params: { date: timelineDate } }),
    enabled: isDay,
  });
  const trace = data?.trace ?? [];
  const visits = data?.visits ?? [];
  const attendance = data?.attendance ?? [];
  const last = data?.lastLocation;
  const summary = data?.summary;
  const tracePts: [number, number][] = trace.map((t: any) => [t.lat, t.lng]);
  // GPS pings grouped into one run per calendar day (see the polyline note in the map below).
  const traceRuns: [number, number][][] = useMemo(() => {
    const runs = new Map<string, [number, number][]>();
    for (const t of trace) {
      const d = toIstDateString(new Date(String(t.at).replace(' ', 'T')));
      if (!runs.has(d)) runs.set(d, []);
      runs.get(d)!.push([t.lat, t.lng]);
    }
    return [...runs.values()].filter((r) => r.length > 1);
  }, [data]);
  const allPts: [number, number][] = useMemo(() => [
    ...tracePts,
    ...visits.map((v: any) => [v.lat, v.lng] as [number, number]),
    ...attendance.map((a: any) => [a.lat, a.lng] as [number, number]),
  ], [data]);
  // The day's path reconstructed from the ordered marks (check-in → reached points → check-out),
  // de-duping consecutive same-spot stops so the line + arrows stay meaningful. Used to draw a route
  // when there's no dense GPS trace (the web/on-behalf flow captures taps, not a continuous stream).
  const markPts: [number, number][] = useMemo(() => {
    const seq: [number, number][] = [];
    for (const s of (day?.stops ?? [])) {
      if (s?.lat == null || s?.lng == null) continue;
      const prev = seq[seq.length - 1];
      if (!prev || prev[0] !== s.lat || prev[1] !== s.lng) seq.push([s.lat, s.lng]);
    }
    return seq;
  }, [day]);
  // Day view with a stop sequence → number the markers + timeline in step (1 … check-out).
  const numberStops = isDay && (day?.stops?.length ?? 0) > 0;
  // Cross-highlight: the stop the user picked (from the timeline or a marker) is enlarged + centred.
  const [activeStop, setActiveStop] = useState<number | null>(null);
  const markerRefs = useRef(new Map<number, any>());
  useEffect(() => { setActiveStop(null); markerRefs.current.clear(); }, [spId, effFrom, effTo]);
  // Reset the day jump if the caller hands us a different person or period.
  useEffect(() => { setActiveDate(null); setShowFullRange(false); }, [spId, from, to]);
  // The header keeps naming the period the CALLER asked for; the in-modal note says which day is shown.
  const when = isRange ? `${formatIstDate(from)} → ${formatIstDate(to)}` : formatIstDate(from);

  return (
    <Modal open width={840} onOpenChange={(o) => { if (!o) onClose(); }}
      title={<span><RouteIcon size={16} /> Route · <b>{name}</b> <Typography.Text type="secondary" className="text-xs" style={{ fontWeight: 400 }}>{when}</Typography.Text></span>}>
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
      ) : allPts.length === 0 ? (
        <Empty description="No location data for this period (no GPS pings, visits or geo-tagged attendance)." />
      ) : (
        <>
          {isDay ? (day?.narrative ? (
            <>
              <div className="mb-2 rounded-lg border border-line bg-surface-2/50 p-3 text-sm leading-relaxed text-content">
                <div className="mb-1 flex items-center gap-1.5 type-overline text-muted"><Sparkles size={12} /> Day summary</div>
                {day.narrative}
              </div>
              {day.totals && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  <StatChip value={`${day.totals.distanceKm} km`} label="travelled" />
                  <StatChip value={fmtMin(day.totals.movingMin)} label="driving" />
                  <StatChip value={fmtMin(day.totals.dwellMin)} label="at stops" />
                  <StatChip value={day.totals.visits} label={`visit${day.totals.visits === 1 ? '' : 's'}`} />
                  {day.totals.unplannedStops > 0 && <StatChip value={day.totals.unplannedStops} label="unplanned" tone="amber" />}
                </div>
              )}
            </>
          ) : (
            <div className="mb-2 flex items-center gap-2 text-sm text-muted"><Sparkles size={14} className="animate-pulse" /> Generating day summary…</div>
          )) : summary && (summary.pings > 0 || summary.stops > 0) ? (
            <Typography.Paragraph style={{ marginBottom: 4 }}>
              <b>Movement:</b> {summary.distanceKm} km travelled · {summary.stops} stop{summary.stops === 1 ? '' : 's'}
              {summary.activeFrom && <> · active {formatIstTime(summary.activeFrom)}–{formatIstTime(summary.activeTo)}</>}
              {summary.truncated && (
                <><br /><Typography.Text type="warning" className="text-xs">
                  This period has more location data than one view can load — the trail stops partway
                  and the distance is a minimum. Pick a single day for the complete picture.
                </Typography.Text></>
              )}
            </Typography.Paragraph>
          ) : null}
          {/* A range has no single timeline (see the note where activeDate is declared) — offer the
              day instead, so looking at someone over a period is never a dead end. */}
          {isRange && (
            <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
              {timelineDate ? (
                <>
                  {dayOptions.length > 1 ? (
                    <>
                      <Typography.Text type="secondary" className="text-xs">Day:</Typography.Text>
                      <select
                        value={timelineDate}
                        aria-label="Day"
                        onChange={(e) => setActiveDate(e.target.value)}
                        className="rounded-md border border-line bg-surface-1 px-2 py-1 type-label-strong text-content"
                      >
                        {dayOptions.map((d) => <option key={d} value={d}>{formatIstDate(d)}</option>)}
                      </select>
                      <Typography.Text type="secondary" className="text-xs">
                        of {formatIstDate(from)} → {formatIstDate(to)} ({dayOptions.length} active days)
                      </Typography.Text>
                    </>
                  ) : (
                    <Typography.Text type="secondary" className="text-xs">
                      All activity in this period is on <b>{formatIstDate(timelineDate)}</b>.
                    </Typography.Text>
                  )}
                  <button
                    type="button"
                    onClick={() => { setActiveDate(null); setShowFullRange(true); }}
                    className="inline-flex items-center gap-1 rounded-md border border-line bg-surface-2 px-2 py-0.5 type-label-strong text-content hover:bg-surface-3"
                  >
                    Whole period trail
                  </button>
                </>
              ) : (
                <>
                  <Typography.Text type="secondary" className="text-xs">
                    Trail for the whole period — a timeline belongs to one day:
                  </Typography.Text>
                  <select
                    value=""
                    aria-label="Back to a day"
                    onChange={(e) => { if (e.target.value) { setActiveDate(e.target.value); setShowFullRange(false); } }}
                    className="rounded-md border border-line bg-surface-1 px-2 py-1 text-xs text-content"
                  >
                    <option value="">Back to a day…</option>
                    {dayOptions.map((d) => <option key={d} value={d}>{formatIstDate(d)}</option>)}
                  </select>
                </>
              )}
            </div>
          )}
          {last && (
            <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              <span><b>Last seen:</b> {last.label} · {formatIst(last.at)}</span>
              <a
                href={`https://www.google.com/maps?q=${last.lat},${last.lng}`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-md bg-brand-50 px-2 py-0.5 type-label-strong text-brand-700! no-underline hover:bg-brand-100 dark:bg-brand-900/30 dark:text-brand-300! dark:hover:bg-brand-900/50"
              >
                <ExternalLink size={12} /> Open in Google Maps
              </a>
            </div>
          )}
          <div style={{ height: 460, borderRadius: 8, overflow: 'hidden' }}>
            <MapContainer center={[22.5, 80]} zoom={5} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
              <TileLayer url={MAP_TILE_URL} attribution={MAP_TILE_ATTRIBUTION} />
              <MapReady pts={allPts} />
              {/* ONE line PER DAY, never one line across the range: joining the last ping of one day
                  to the first of the next draws an overnight straight line the rep never travelled
                  (Pune → Nagpur across the map). Each day's trail is its own polyline. */}
              {traceRuns.map((run, ri) => (
                <Polyline key={`run${ri}`} positions={run} pathOptions={{ color: 'var(--color-sky-500)', weight: 3, opacity: 0.8 }} />
              ))}
              {traceRuns.map((run, ri) => {
                if (run.length <= 3) return null;
                const step = Math.max(2, Math.floor(run.length / 14));
                const arr: any[] = [];
                for (let i = step; i < run.length; i += step)
                  arr.push(<Marker key={`ar${ri}-${i}`} position={run[i]} icon={arrowIcon(bearingDeg(run[i - 1], run[i]))} interactive={false} />);
                return arr;
              })}
              {/* No dense GPS trace? Reconstruct the day's PATH from the ordered visit marks
                  (check-in → each reached point → check-out). The 3-mark lifecycle gives a located,
                  time-ordered route without needing the Phase-5 GPS stream — drawn dashed to signal
                  it's a marks path, not a raw GPS track. Consecutive same-spot stops are de-duped. */}
              {isDay && tracePts.length < 2 && markPts.length > 1 && (
                <Polyline positions={markPts} pathOptions={{ color: STOP_HEX.VISIT, weight: 3, opacity: 0.65, dashArray: '4 9' }} />
              )}
              {/* Day view: ONE numbered marker per stop, in the same order as the timeline (1 = first
                  action → last = check-out), so a marker on the map maps to its timeline row. */}
              {numberStops && <FocusStop activeStop={activeStop} getMarker={(i) => markerRefs.current.get(i)} />}
              {numberStops
                ? <NumberedStops
                    stops={day?.stops ?? []}
                    activeStop={activeStop}
                    onPick={setActiveStop}
                    register={(i, m) => markerRefs.current.set(i, m)}
                  />
                : (<>
                    {visits.map((v: any, i: number) => (
                      <Marker key={`v${i}`} position={[v.lat, v.lng]} icon={dot(VISIT_HEX[v.type] ?? STOP_HEX.VISIT)}>
                        <Popup><b>{v.name}</b><br />{v.type === 'RETAILER' ? 'Retailer' : v.type === 'FARMER' ? 'Farmer' : 'Visit'} · {formatIst(v.at)}</Popup>
                      </Marker>
                    ))}
                    {attendance.map((a: any, i: number) => (
                      <Marker key={`a${i}`} position={[a.lat, a.lng]} icon={dot(a.kind === 'IN' ? STOP_HEX.CHECK_IN : STOP_HEX.CHECK_OUT)}>
                        <Popup>{a.kind === 'IN' ? 'Check-in' : 'Check-out'}<br />{formatIst(a.at)}</Popup>
                      </Marker>
                    ))}
                  </>)}
              {/* "Last seen" = latest known position — a distinct hollow ring drawn ON TOP of everything
                  (non-interactive so it never steals a numbered stop's click) so it's always visible even
                  when it sits on the check-out / last visit. */}
              {last && <Marker position={[last.lat, last.lng]} icon={lastSeenIcon} interactive={false} zIndexOffset={2000} />}
            </MapContainer>
          </div>
          {isDay && day?.stops?.length > 0 && (
            <div className="mt-3">
              <div className="mb-1.5 type-body-strong text-content">Timeline</div>
              <div>
                {day.stops.map((s: any, i: number) => {
                  const seg = i > 0 ? day.segments[i - 1] : null;
                  return (
                    <div key={i}>
                      {seg && (
                        <div className="ml-2.5 flex items-center gap-1.5 border-l-2 border-dashed border-line py-0.5 pl-4.5 text-xs text-muted">
                          <RouteIcon size={12} /> {seg.distanceKm} km · {fmtMin(seg.actualMin)}
                          {seg.delayMin >= 15 && <span className="text-amber-600 dark:text-amber-400">· {fmtMin(seg.delayMin)} over expected</span>}
                        </div>
                      )}
                      <div
                        className={cn(
                          '-mx-1 flex cursor-pointer items-center gap-2.5 rounded-md px-1 py-1.5 transition-colors',
                          i === activeStop ? 'bg-brand-50 dark:bg-brand-900/25' : 'hover:bg-surface-2',
                        )}
                        onClick={() => setActiveStop(i)}
                        title="Show this stop on the map"
                      >
                        <span
                          className={cn(
                            'flex size-5 shrink-0 items-center justify-center rounded-full type-caption-strong leading-none text-white ring-2',
                            i === activeStop ? 'ring-brand-500 dark:ring-brand-300' : 'ring-surface',
                          )}
                          style={{ background: stopColor(s) }}
                        >{i + 1}</span>
                        <span className="w-16 shrink-0 text-xs tabular-nums text-muted">{formatIstTime(s.at)}</span>
                        <span className="flex-1 text-sm text-content">
                          {s.label}
                          {s.kind === 'VISIT' && s.visitType && <Tag tone={s.visitType === 'RETAILER' ? 'blue' : 'purple'} className="ml-1.5 align-middle">{s.visitType === 'RETAILER' ? 'Retailer' : 'Farmer'}</Tag>}
                          {s.kind === 'VISIT' && (s.photosCount ?? 0) > 0 && <span className="ml-1.5 inline-flex items-center gap-0.5 align-middle text-xs text-muted"><Camera size={12} />{s.photosCount}</span>}
                          {s.kind === 'CHECK_IN' && isDayStart(day.stops, i) && <span className="ml-1 text-xs text-muted">· Start</span>}
                          {s.kind === 'CHECK_OUT' && isDayEnd(day.stops, i) && <span className="ml-1 text-xs text-muted">· End</span>}
                          {s.kind === 'UNPLANNED' && <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">· not a visit</span>}
                        </span>
                        {s.dwellMin != null && s.dwellMin > 0 && <span className="shrink-0 text-xs text-muted">{fmtMin(s.dwellMin)}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <Space size={16} className="mt-4 border-t border-line pt-3 text-xs" wrap>
            {isDay && tracePts.length < 2 && markPts.length > 1
              ? <span><span style={{ color: STOP_HEX.VISIT }}>┄</span> Route via visit marks ({markPts.length} stops)</span>
              : <span title="Raw GPS breadcrumb points sampled by the mobile app for this day"><span style={{ color: 'var(--color-sky-500)' }}>▬</span> GPS trail ({trace.length} pings)</span>}
            <span><span style={{ color: STOP_HEX.CHECK_IN }}>●</span> Check-in</span>
            <span><span style={{ color: VISIT_HEX.RETAILER }}>●</span> Retailer ({visits.filter((v: any) => v.type === 'RETAILER').length})</span>
            <span><span style={{ color: VISIT_HEX.FARMER }}>●</span> Farmer ({visits.filter((v: any) => v.type === 'FARMER').length})</span>
            {isDay && (day?.totals?.unplannedStops ?? 0) > 0 && <span><span style={{ color: STOP_HEX.UNPLANNED }}>●</span> Unplanned stop</span>}
            <span><span style={{ color: STOP_HEX.CHECK_OUT }}>●</span> Check-out</span>
            <span><span style={{ color: LAST_SEEN_HEX }}>◍</span> Last seen</span>
          </Space>
        </>
      )}
    </Modal>
  );
}
